/**
 * Live waveform + timer while recording / transcribing with Whisper.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

const BAR_COUNT = 32;

function formatVoiceTime(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}

export default function VoiceRecordingBar({
  isListening,
  isTranscribing,
  recordingMs = 0,
  audioLevels = [],
  onStop,
  onCancel,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.voiceRecordingBar.${key}`, opts);

  const levels =
    audioLevels.length >= BAR_COUNT
      ? audioLevels.slice(0, BAR_COUNT)
      : [
          ...audioLevels,
          ...Array.from({ length: BAR_COUNT - audioLevels.length }, () => 0.12),
        ];

  const label = isTranscribing ? p('transcribing') : p('speak_prompt');

  return (
    <div
      className="flex items-center gap-2 min-h-[44px] py-0.5 px-0.5"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div
        className={`flex-1 min-w-0 flex items-center gap-2.5 px-3.5 py-2 rounded-full border ${
          isTranscribing
            ? 'bg-blue-500/10 border-blue-400/25'
            : 'bg-slate-400/15 border-slate-400/20'
        }`}
      >
        <div className="flex-1 min-w-0 flex items-center gap-0.5 h-6 overflow-hidden" aria-hidden="true">
          {levels.map((level, index) => (
            <span
              key={index}
              className={`w-0.5 rounded-full bg-slate-400 transition-all duration-75 ${
                isTranscribing ? 'animate-pulse' : ''
              }`}
              style={{
                height: `${Math.max(3, Math.round(level * 22))}px`,
                opacity: isTranscribing ? 0.45 : 0.55 + level * 0.45,
                animationDelay: isTranscribing && index % 2 ? '150ms' : undefined,
              }}
            />
          ))}
        </div>
        <span
          className={`shrink-0 font-semibold tabular-nums text-slate-300 text-right ${
            isTranscribing ? 'min-w-[7.5rem] text-xs font-medium text-blue-300' : 'min-w-10 text-xs'
          }`}
        >
          {isTranscribing ? label : formatVoiceTime(recordingMs)}
        </span>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {isListening && (
          <button
            type="button"
            className="w-[34px] h-[34px] shrink-0 border border-white/20 rounded-full bg-white/5 text-slate-300 cursor-pointer grid place-items-center transition-colors hover:bg-red-500/15 hover:border-red-400/35 hover:text-red-300"
            onClick={onCancel}
            aria-label={p('cancel_recording_aria')}
            title={p('cancel')}
          >
            <X size={16} />
          </button>
        )}

        {isListening ? (
          <button
            type="button"
            className="w-[34px] h-[34px] shrink-0 border-none rounded-full bg-slate-200 cursor-pointer grid place-items-center transition-transform hover:bg-slate-50 hover:scale-105"
            onClick={onStop}
            aria-label={p('stop_transcribe_aria')}
            title={p('stop_transcribe_title')}
          >
            <span className="w-3 h-3 rounded-sm bg-slate-900" aria-hidden="true" />
          </button>
        ) : (
          <div
            className="w-[34px] h-[34px] shrink-0 rounded-full bg-slate-200 opacity-85 grid place-items-center pointer-events-none"
            aria-hidden="true"
          >
            <span className="w-3.5 h-3.5 rounded-full border-2 border-slate-900/15 border-t-slate-900 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}

export { formatVoiceTime, BAR_COUNT };
